import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_IMAGE,
  SITE_NAME,
  getCanonicalUrl,
  getRouteMeta,
  getStructuredData,
} from "../../seo/siteMetadata.js";

const ensureTag = (selector, createTag) => {
  let node = document.head.querySelector(selector);
  if (!node) {
    node = createTag();
    document.head.appendChild(node);
  }
  return node;
};

const setMetaByName = (name, content) => {
  const node = ensureTag(`meta[name='${name}']`, () => {
    const element = document.createElement("meta");
    element.setAttribute("name", name);
    return element;
  });
  node.setAttribute("content", content);
};

const setMetaByProperty = (property, content) => {
  const selector = `meta[property='${property}']`;
  if (!content) {
    document.head.querySelector(selector)?.remove();
    return;
  }

  const node = ensureTag(selector, () => {
    const element = document.createElement("meta");
    element.setAttribute("property", property);
    return element;
  });
  node.setAttribute("content", content);
};

const setCanonical = (href) => {
  const selector = "link[rel='canonical']";
  if (!href) {
    document.head.querySelector(selector)?.remove();
    return;
  }

  const node = ensureTag(selector, () => {
    const element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    return element;
  });
  node.setAttribute("href", href);
};

const setStructuredData = (pathname) => {
  document.querySelectorAll("script[data-salespartner-schema]").forEach((node) => node.remove());

  getStructuredData(pathname).forEach((value, index) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.dataset.salespartnerSchema = `${pathname}-${index}`;
    script.textContent = JSON.stringify(value);
    document.head.appendChild(script);
  });
};

export function SeoHead() {
  const location = useLocation();

  useEffect(() => {
    // Keep server metadata for this exact shared URL; resume normal SEO on navigation.
    const sharedRequest = document.head.querySelector("meta[name='salespartner:share-request']");
    if (sharedRequest?.content === location.pathname + location.search) return;
    sharedRequest?.remove();
    const pathname = location.pathname;
    const meta = getRouteMeta(pathname);
    const canonical = getCanonicalUrl(pathname);

    document.title = meta.title;
    setMetaByName("description", meta.description);
    setMetaByName("robots", meta.robots);

    setMetaByProperty("og:title", meta.title);
    setMetaByProperty("og:description", meta.description);
    setMetaByProperty("og:type", "website");
    setMetaByProperty("og:url", canonical);
    setMetaByProperty("og:image", DEFAULT_IMAGE);
    setMetaByProperty("og:image:width", "1200");
    setMetaByProperty("og:image:height", "630");
    setMetaByProperty("og:locale", "sv_SE");
    setMetaByProperty("og:site_name", SITE_NAME);

    setMetaByName("twitter:card", "summary_large_image");
    setMetaByName("twitter:title", meta.title);
    setMetaByName("twitter:description", meta.description);
    setMetaByName("twitter:image", DEFAULT_IMAGE);

    setCanonical(canonical);
    setStructuredData(pathname);
  }, [location.pathname, location.search]);

  return null;
}
